import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, ITicketingState, TicketingActionCreators, ITicketResponse, ICenterState, ITicketLookupDataResponse } from '@nkr/store/index';
import { IListItem } from '@nkr/models/index';
import { TicketType, TicketUrgency } from '@nkr/enums/index';
import { Button, FormControl, FormGroup, ControlLabel, HelpBlock, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MyUploader from '../../file-upload';

interface IProps {
    id: number,
    centerId: number
}

interface IStateProps {
    ticketing: ITicketingState,
    centers: ICenterState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getTicket: (ticketId: number) => Promise<ITicketResponse>,
    getLookupData: () => Promise<ITicketLookupDataResponse>,
    saveCenterTicket: (subject: string, message: string, ticketType: TicketType, urgency: TicketUrgency, files: File[]) => Promise<ITicketResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    subject: string,
    subjectValid: boolean,
    message: string,
    messageValid: boolean,
    ticketType: TicketType,
    ticketTypeValid: boolean,
    urgency: number,
    urgencyValid: boolean,
    files: File[],
    ticketTypeList: IListItem[],
    ticketUrgencyList: IListItem[],
    dirty: boolean
}

class CreateCenterTicket extends React.Component<TProps, IState> {
    readonly state: IState = {
        subject: '',
        subjectValid: true,
        message: '',
        messageValid: true,
        ticketType: null,
        ticketTypeValid: true,
        urgency: 0,
        urgencyValid: true,
        files: [],
        ticketTypeList: [],
        ticketUrgencyList: [],
        dirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    handleReturnToList = () => {
        window.location.href = `/center/${this.props.centerId}/action-center/support`;
    }

    handleSubjectChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            subject: e.currentTarget.value
        });
    }

    handleMessageChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            message: e.currentTarget.value
        });
    }

    handleTicketTypeChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            ticketType: parseInt(e.currentTarget.value)
        });
    }

    handleUrgencyChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            urgency: parseInt(e.currentTarget.value)
        });
    }

    handleFilesChanged = (file: File, status: string) => {
        let files = [...this.state.files];

        if (status == 'done') {
            files.push(file);
        }

        if (status == 'removed') {
            files = files.filter(x => x.name != file.name);
        }

        this.setState({
            files
        });
    }

    handleSubmit = () => {
        let { subjectValid, messageValid, ticketTypeValid, urgencyValid } = this.validate();
        let isValid = subjectValid && messageValid && ticketTypeValid && urgencyValid;

        this.setState({
            subjectValid,
            messageValid,
            ticketTypeValid,
            urgencyValid,
            dirty: true
        }, () => {
            if (isValid) {
                let { subject, message, ticketType, urgency, files } = this.state;

                this.props.showLoading('Creating ticket, one moment please...').then(() => {
                    this.props.saveCenterTicket(subject, message, ticketType, urgency, files).then(response => {
                        if (response.data.success) {
                            this.props.hideLoading();

                            toast.success('Ticket submitted, going back to the ticket list...', {
                                onClose: () => {
                                    window.location.href = `/center/${this.props.centerId}/action-center/support`;
                                }
                            })
                        } else {
                            toast.error('Unable to submit your ticket...');
                        }
                    });
                });
            }
        });
    }

    validate = () => {
        let { subject, message, ticketType, urgency } = this.state;

        return {
            subjectValid: subject != '',
            messageValid: message != '',
            ticketTypeValid: ticketType > 0,
            urgencyValid: urgency >= 0
        };
    }

    getData = () => {
        this.props.showLoading('Loading ticket, one moment please...').then(() => {
            return Promise.all([
                this.props.getTicket(this.props.id),
                this.props.getLookupData()
            ]).then((response: [ITicketResponse, ITicketLookupDataResponse]) => {
                if (response[1].data.success) {
                    this.setState({
                        ticketTypeList: response[1].data.results.ticketTypeList,
                        ticketUrgencyList: response[1].data.results.ticketUrgencyList
                    }, () => {
                        this.props.hideLoading();
                    });
                }
            });
        });
    }

    render = () => {
        const { subjectValid, messageValid, ticketTypeValid, urgencyValid, dirty } = this.state;
        const isValid = dirty ? subjectValid && messageValid && ticketTypeValid && urgencyValid : true;

        return (
            <>
                <div className='container' style={{ marginBottom: 15 }}>
                    {!isValid && (
                        <div className='row'>
                            <div className='col-xs-12 col-lg-12'>
                                <Alert bsStyle='danger'>Please complete the items in red below</Alert>
                            </div>
                        </div>
                    )}

                    <div className='row'>
                        <div className='col-xs-12 col-lg-12'>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <h1 style={{ marginBottom: 30 }}>Create Ticket</h1>
                                </div>
                                <div>
                                    <Button bsStyle='link' onClick={this.handleReturnToList}>Return to the List</Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-xs-12 col-lg-8'>
                            <FormGroup validationState={!subjectValid ? 'error' : null}>
                                <ControlLabel>Subject</ControlLabel>
                                <FormControl
                                    value={this.state.subject}
                                    onChange={this.handleSubjectChange}
                                />

                                {!subjectValid && (
                                    <HelpBlock>Subject is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup validationState={!messageValid ? 'error' : null}>
                                <ControlLabel>Message</ControlLabel>
                                <FormControl
                                    componentClass='textarea'
                                    style={{ height: 200, resize: 'none' }}
                                    value={this.state.message}
                                    onChange={this.handleMessageChange}
                                />

                                {!messageValid && (
                                    <HelpBlock>Message is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup validationState={!ticketTypeValid ? 'error' : null}>
                                <ControlLabel>Type</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    value={this.state.ticketType}
                                    onChange={this.handleTicketTypeChange}
                                >
                                    <option value={-1}>- Select Type -</option>
                                    {this.state.ticketTypeList.map((item, index) =>
                                        <option key={`tt-${index}`} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>

                                {!this.state.ticketTypeValid && (
                                    <HelpBlock>Type is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup validationState={!urgencyValid ? 'error' : null}>
                                <ControlLabel>Urgency</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    value={this.state.urgency}
                                    onChange={this.handleUrgencyChange}
                                >
                                    <option value={-1}>- Select Type -</option>
                                    {this.state.ticketUrgencyList.map((item, index) =>
                                        <option key={`tu-${index}`} value={item.id}>{item.description}</option>
                                    )}
                                </FormControl>

                                {!urgencyValid && (
                                    <HelpBlock>Urgency is required</HelpBlock>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <ControlLabel>Attachments</ControlLabel>
                                <div style={{ border: '1px solid #ddd', borderRadius: 4 }}>
                                    <MyUploader hideBorder={true} filesChanged={this.handleFilesChanged} />
                                </div>
                            </FormGroup>

                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 30 }}>
                                <Button bsStyle='primary' bsSize='large' onClick={this.handleSubmit}>Submit Ticket</Button>
                                <Button bsStyle='link' onClick={this.handleReturnToList}>Cancel &amp; Return to the List</Button>
                            </div>
                        </div>
                        <div className='col-xs-12 col-lg-4' style={{ marginTop: 25 }}>
                            <div className='alert alert-info'>
                                Please help us improve our system by telling us about any enhancements you would like to see or any issues you might be encountering.
                                <br /><br />
                                When submitted, this ticket will go directly to NKR.
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        ticketing: state.ticketing,
        centers: state.centers
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch<any>(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch<any>(AppActionCreators.hideLoading());
        },
        getTicket: (ticketId) => {
            return dispatch<any>(TicketingActionCreators.getTicket(ticketId));
        },
        getLookupData: () => {
            return dispatch<any>(TicketingActionCreators.getLookupData());
        },
        saveCenterTicket: (subject, message, ticketType, urgency, files) => {
            return dispatch<any>(TicketingActionCreators.saveCenterTicket(subject, message, ticketType, urgency, files));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CreateCenterTicket);
